/**
 * Theme sync: 2026-09-03-base
 * jrs-v2-step-logo-strategy.js — Logo strategy step (Phase 2.5 rebuild)
 *
 * Doc 6 §11.1 step id `logo_strategy`. CONDITIONAL — only rendered when
 * state.draft.positions.length >= 2. The multistep + bundle flow
 * orchestrators and the application_method controller all honour this.
 *
 * Phase 2.5 layout per mockups/step3-logo-strategy-{desktop,mobile}.html.
 * Shares the .jrs-card-choice DOM/CSS pattern with branding_strategy
 * (commit 3) — one card-choice rule set in jrs-v2-steps.css serves both
 * controllers.
 *
 * Two choices per Doc 7:
 *   - 'same_logo'        — one logo applied to all positions (recommended)
 *   - 'different_logos'  — logo picker runs per position
 *
 * State path per Doc 6 §5.3:
 *   state.draft.logo_strategy = 'same_logo' | 'different_logos'
 *
 * NOTE: the mockup uses data-value="per_position" on the second card,
 * but the state contract is 'different_logos' (locked by Doc 6 §5.3 +
 * the logo_picker controller's cursor logic + commit b8e6565). We keep
 * the contract value; the mockup's `per_position` was a design label
 * never exposed to the customer.
 */

window.JRS = window.JRS || {};
window.JRS.Step = window.JRS.Step || {};

(function (JRS) {
  'use strict';

  var STEP_ID = 'logo_strategy';

  var _mountEl = null;
  var _listeners = [];

  function onEnter(state, context) {
    _mountEl = context.mountEl;
    if (!_mountEl) return;

    JRS.CustomizerPopup.setReassurance(
      'Free design proof on every order. Change your mind any time.'
    );
    JRS.CustomizerPopupState.dispatch({ type: 'HIDE_SUMMARY' });

    var draft = state.draft || {};
    var current = draft.logo_strategy || null;
    // Preselect same_logo so the step opens resolved rather than blank
    // (standing decision: the first option is always selected). Only when
    // NOTHING is set, so a restored / cart-edit / back-navigation value always
    // wins. A default is not a user decision, so this fires no telemetry —
    // same rule the application_method step's method seeding follows.
    if (current !== 'same_logo' && current !== 'different_logos') {
      current = 'same_logo';
      JRS.CustomizerPopupState.dispatch({
        type: 'SET_DRAFT_FIELD',
        payload: { field: 'logo_strategy', value: current }
      });
    }
    var positionCount = (draft.positions || []).length;
    _render(current, positionCount, _categoryProgress(state), _selectedPositionNames(state));
    _bindInteractions();
    _syncCta(true);
  }

  function onExit(state, context) {
    _unbindListeners();
    _mountEl = null;
  }

  function onValidate(state, context) {
    var s = state.draft && state.draft.logo_strategy;
    if (s !== 'same_logo' && s !== 'different_logos') {
      return { valid: false, errors: { logo_strategy: 'required' } };
    }
    return { valid: true };
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  // per_product: returns the category context (name + "{n} of {N}" cursor) for
  // the header; bundle_wide returns { perProduct: false } so the header renders
  // unchanged. Mirrors position_picker._headerInfo (spec D5).
  function _categoryProgress(state) {
    var draft = (state && state.draft) || {};
    if (draft.branding_strategy !== 'per_product') return { perProduct: false };
    var active = (JRS.CustomizerPopupState && typeof JRS.CustomizerPopupState.getActiveCategoryList === 'function')
      ? JRS.CustomizerPopupState.getActiveCategoryList() : [];
    var cursor = (typeof draft._current_category_index === 'number' && draft._current_category_index >= 0)
      ? draft._current_category_index : 0;
    var cat = active[cursor];
    return {
      perProduct: true,
      categoryName: (cat && cat.name) || 'this product',
      current: cursor + 1,
      total: active.length || 1
    };
  }

  /**
   * The selected positions' display names, in the SAME order step 2 and step 3
   * list them — the picker's resolved catalogue (alphabetical, Left Breast
   * pinned, free positions floated), not the order the customer tapped.
   * Returns null when the picker module is unavailable, when the catalogue is
   * empty, or when ANY selected id has no name in it: the caller then falls
   * back to the count sentence rather than rendering a gap or a partial list.
   */
  function _selectedPositionNames(state) {
    var draft = (state && state.draft) || {};
    var selected = draft.positions || [];
    if (!selected.length) return null;

    var picker = JRS.Step && JRS.Step.PositionPicker;
    if (!picker || typeof picker._resolveActiveCatalogue !== 'function') return null;
    var catalogue;
    try { catalogue = picker._resolveActiveCatalogue(state || {}) || []; }
    catch (e) { return null; }
    if (!catalogue.length) return null;

    var names = [];
    for (var c = 0; c < catalogue.length; c++) {
      if (selected.indexOf(catalogue[c].id) === -1) continue;
      var name = catalogue[c].name;
      if (typeof name !== 'string' || !name.length) return null;
      names.push(name);
    }
    // A selected position the catalogue doesn't carry (scope mismatch, a
    // cart-edit leftover) would silently vanish from the sentence.
    if (names.length !== selected.length) return null;
    return names;
  }

  // "A and B" / "A, B and C" / "A, B, C and D".
  function _joinNames(names) {
    if (names.length === 1) return names[0];
    return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  }

  function _render(current, positionCount, headerInfo, positionNames) {
    // Name the positions rather than counting them. Falls back to the count
    // sentence whenever the names can't be resolved in full.
    var subtitle;
    if (positionNames && positionNames.length) {
      subtitle = 'You picked ' + _joinNames(positionNames) + '.';
    } else if (positionCount > 0) {
      subtitle = 'You picked ' + positionCount + ' positions.';
    } else {
      subtitle = '';
    }

    // The heading and the same-logo card both agree with the count: "both" at
    // two positions, the number itself above that. The step is conditional on
    // >= 2 positions, so the < 2 branch is defensive only.
    var heading;
    var sameDesc;
    if (positionCount === 2) {
      heading = 'Same logo on both?';
      sameDesc = 'One logo on both positions. Most company kit works this way.';
    } else if (positionCount >= 3) {
      heading = 'Same logo on all ' + positionCount + '?';
      sameDesc = 'One logo on all ' + positionCount + ' positions. Most company kit works this way.';
    } else {
      heading = 'Same logo for all positions?';
      sameDesc = 'One logo on every position. Most company kit works this way.';
    }

    var differentTime = positionCount > 0
      ? '~1 min × ' + positionCount + ' positions'
      : '~1 min per position';

    headerInfo = headerInfo || { perProduct: false };
    // per_product: a "{n} of {N}" progress pill above the title + the category
    // name as the sub-label (which product this strategy applies to). Reuses
    // the existing .jrs-step__progress-pill pattern (spec D5).
    var pillHtml = headerInfo.perProduct
      ? '<p class="jrs-step__progress-pill">' + headerInfo.current + ' of ' + headerInfo.total + '</p>'
      : '';
    if (headerInfo.perProduct) subtitle = headerInfo.categoryName;
    var subtitleHtml = subtitle
      ? '<p class="jrs-step__subtitle">' + _escape(subtitle) + '</p>'
      : '';

    _mountEl.innerHTML =
      '<div class="jrs-step jrs-step--logo-strategy" data-jrs-step="logo_strategy">' +
      '  <header class="jrs-step__header' + (headerInfo.perProduct ? ' jrs-step__header--per-position' : '') + '">' +
           pillHtml +
      '    <h2 class="jrs-step__title">' + _escape(heading) + '</h2>' +
           subtitleHtml +
      '  </header>' +
      '  <section class="jrs-step__body">' +
      '    <div class="jrs-card-choices" role="radiogroup" aria-label="Logo strategy">' +
           _renderCard({
             value: 'same_logo',
             badge: 'Recommended',
             title: 'Same logo',
             desc: sameDesc,
             metaTime: '~1 minute',
             selected: current === 'same_logo'
           }) +
           _renderCard({
             value: 'different_logos',
             badge: null,
             title: 'Different logo for each',
             desc: 'Your company logo on the chest, an accreditation on the sleeve, that sort of thing.',
             metaTime: differentTime,
             selected: current === 'different_logos'
           }) +
      '    </div>' +
      '  </section>' +
      '</div>';
  }

  function _renderCard(opts) {
    var badgeHtml = opts.badge
      ? '<span class="jrs-card-choice__badge">' + _escape(opts.badge) + '</span>'
      : '';
    return (
      '<button type="button"' +
      '        class="jrs-card-choice' + (opts.selected ? ' jrs-card-choice--selected' : '') + '"' +
      '        role="radio"' +
      '        aria-checked="' + (opts.selected ? 'true' : 'false') + '"' +
      '        data-jrs-action="strategy-choice"' +
      '        data-jrs-value="' + _escape(opts.value) + '"' +
      '        tabindex="' + (opts.selected ? '0' : '-1') + '">' +
        badgeHtml +
      '  <span class="jrs-card-choice__radio" aria-hidden="true"></span>' +
      '  <h3 class="jrs-card-choice__title">' + _escape(opts.title) + '</h3>' +
      '  <p class="jrs-card-choice__desc">' + _escape(opts.desc) + '</p>' +
      '  <div class="jrs-card-choice__meta">' +
      '    <svg class="jrs-card-choice__meta-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
      '      <circle cx="8" cy="8" r="6.5"/>' +
      '      <path d="M8 4.5 L8 8 L10.5 9.5"/>' +
      '    </svg>' +
      '    <span class="jrs-card-choice__meta-time">' + _escape(opts.metaTime) + '</span>' +
      '  </div>' +
      '</button>'
    );
  }

  // ---------------------------------------------------------------------
  // Interactions
  // ---------------------------------------------------------------------

  function _bindInteractions() {
    if (!_mountEl) return;
    var buttons = _mountEl.querySelectorAll('[data-jrs-action="strategy-choice"]');
    for (var i = 0; i < buttons.length; i++) {
      _bind(buttons[i], 'click', _onChoiceClick);
    }
  }

  // Selecting a card records the choice and marks the card. It no longer
  // advances the step: an option is always selected now (same_logo is
  // preselected on mount), so the customer needs a deliberate press to move on,
  // and can change their mind first. The chrome-footer CTA does the advancing.
  function _onChoiceClick(e) {
    var value = e.currentTarget && e.currentTarget.getAttribute('data-jrs-value');
    if (value !== 'same_logo' && value !== 'different_logos') return;

    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: 'logo_strategy', value: value }
    });

    _markSelected(value);
    _syncCta(false);
  }

  // Patch the two cards in place rather than re-rendering, so the pressed card
  // keeps focus. Mirrors the markup _renderCard emits.
  function _markSelected(value) {
    if (!_mountEl) return;
    var buttons = _mountEl.querySelectorAll('[data-jrs-action="strategy-choice"]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var on = btn.getAttribute('data-jrs-value') === value;
      btn.classList.toggle('jrs-card-choice--selected', on);
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      btn.setAttribute('tabindex', on ? '0' : '-1');
    }
  }

  // Chrome-footer CTA. Always enabled: a strategy is always selected, either
  // the mount-time same_logo default or the customer's own pick. Same shape as
  // position_picker._syncCta — setPrimaryCta on mount, updatePrimaryCta after.
  function _syncCta(initial) {
    var pop = JRS.CustomizerPopup;
    if (!pop) return;
    if (initial && typeof pop.setPrimaryCta === 'function') {
      pop.setPrimaryCta({
        label: 'Add your logo',
        disabled: false,
        onClick: _onContinue
      });
    } else if (typeof pop.updatePrimaryCta === 'function') {
      pop.updatePrimaryCta({ label: 'Add your logo', disabled: false });
    }
  }

  function _onContinue() {
    var state = JRS.CustomizerPopupState.getState();
    var result = onValidate(state, { trigger: 'primary_cta' });
    if (!result.valid) return;

    // The decision event moved from the card tap to this press (2026-09-13).
    // With same_logo preselected, a customer who accepts the default never taps
    // a card, so firing on tap would have lost those decisions entirely. Same
    // event name, same parameters, one per visit to the step.
    JRS.CustomizerPopup.fireAnalytics('pdp_customizer_decision', {
      step_id: STEP_ID,
      decision_type: 'logo_strategy',
      decision_value: (state.draft && state.draft.logo_strategy) || null
    });

    JRS.CustomizerPopup.goToStep('logo_picker', { trigger: 'primary_cta' });
  }

  // ---------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------

  function _bind(el, evt, fn) {
    el.addEventListener(evt, fn);
    _listeners.push({ el: el, evt: evt, fn: fn });
  }

  function _unbindListeners() {
    for (var i = 0; i < _listeners.length; i++) {
      var t = _listeners[i];
      try { t.el.removeEventListener(t.evt, t.fn); } catch (e) {}
    }
    _listeners = [];
  }

  function _escape(s) {
    if (typeof s !== 'string') return '';
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ---------------------------------------------------------------------
  // Public API + register
  // ---------------------------------------------------------------------

  JRS.Step.LogoStrategy = {
    STEP_ID: STEP_ID,
    onEnter: onEnter,
    onExit: onExit,
    onValidate: onValidate,
    _categoryProgress: _categoryProgress
  };

  function _register() {
    if (window.JRS && JRS.CustomizerPopup && typeof JRS.CustomizerPopup.registerStep === 'function') {
      JRS.CustomizerPopup.registerStep(STEP_ID, {
        onEnter: onEnter,
        onExit: onExit,
        onValidate: onValidate
      });
    } else {
      window.JRS = window.JRS || {};
      window.JRS._pendingStepRegistrations = window.JRS._pendingStepRegistrations || [];
      window.JRS._pendingStepRegistrations.push({
        stepId: STEP_ID,
        definition: { onEnter: onEnter, onExit: onExit, onValidate: onValidate }
      });
    }
  }
  _register();
})(window.JRS);
